<template>
  <div class="root">

    <!-- ══ MODAL INTERVENTION ══ -->
    <modal name="modal-intervention" height="auto" :scrollable="true" :adaptive="true">
      <div v-if="selIntervention" class="modal-wrap">
        <div class="modal-header">
          <div class="modal-header-left">
            <span class="modal-ref">{{ selIntervention.reference || 'Intervention' }}</span>
            <h3 class="modal-title">{{ selIntervention.intervention_type.nom }}</h3>
          </div>
          <button class="icon-btn" @click="$modal.hide('modal-intervention')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="selIntervention.statut" class="status-tag" :style="statusStyle(selIntervention.statut)">
            <span class="status-dot" :style="{ background: selIntervention.statut.couleur }"></span>
            {{ selIntervention.statut.libelle }}
          </div>

          <div class="info-grid">
            <div class="info-cell">
              <p class="info-label">Date</p>
              <p class="info-value">{{ fmt(selIntervention.date_intervention) }}</p>
            </div>
            <!-- <div class="info-cell">
              <p class="info-label">Horaire</p>
              <p class="info-value">
                {{ selIntervention.heure_debut || '—' }}
                {{ selIntervention.heure_fin ? '→ ' + selIntervention.heure_fin : '' }}
              </p>
            </div> -->
            <div class="info-cell" v-if="selIntervention.technicien">
              <p class="info-label">Technicien</p>
              <p class="info-value">{{ selIntervention.technicien.prenom }} {{ selIntervention.technicien.nom }}</p>
            </div>
            <div class="info-cell" v-if="selIntervention.appareil">
              <p class="info-label">Appareil</p>
              <p class="info-value">{{ selIntervention.appareil.marque }} {{ selIntervention.appareil.model }}</p>
            </div>
          </div>

          <div v-if="selIntervention.intervention_type.description" class="modal-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ selIntervention.intervention_type.description }}
          </div>

          <div v-if="selIntervention.documents && selIntervention.documents.length" class="modal-section">
            <p class="section-label">Documents</p>
            <div class="doc-list">
              <a v-for="d in selIntervention.documents" :key="d.id" :href="d.url" target="_blank" class="doc-row">
                <span class="doc-icon-wrap">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </span>
                <span class="doc-name">{{ docTypeLabel[d.type] || d.type }}</span>
                <span class="doc-open">Ouvrir →</span>
              </a>
            </div>
          </div>
          <div v-if="selIntervention.signature_client" class="modal-section">
            <p class="section-label">Signature client</p>
            <img :src="selIntervention.signature_client" alt="Signature" class="sig-img" />
          </div>
        </div>
      </div>
    </modal>

    <!-- ══ LOADING ══ -->
    <div v-if="loading" class="loading-state">
      <div class="loader-ring"><div></div><div></div><div></div><div></div></div>
    </div>

    <!-- ══ APP ══ -->
    <div v-else-if="client" class="app-shell">

      <!-- SIDEBAR -->
      <aside class="sidebar">
        <div class="sidebar-top">
          <div class="sidebar-brand">
            <img v-if="client.societe.logo" :src="client.societe.logo" :alt="client.societe.nom" class="brand-logo" />
            <span v-else class="brand-name">{{ client.societe.nom }}</span>
          </div>
          <nav class="side-nav">
            <button v-for="t in tabs" :key="t.id" @click="goTab(t.id)" class="side-link" :class="{ 'side-link--active': activeTab === t.id }">
              <span class="side-ico" v-html="t.svg"></span>
              <span class="side-lbl">{{ t.label }}</span>
              <span v-if="t.count !== undefined && t.count > 0" class="side-badge">{{ t.count }}</span>
            </button>
          </nav>
        </div>
        <div class="sidebar-user">
          <div class="user-avatar">{{ client.prenom[0] }}{{ client.nom[0] }}</div>
          <div class="user-info">
            <p class="user-name">{{ client.prenom }} {{ client.nom }}</p>
            <p class="user-meta">{{ client.email }}</p>
          </div>
        </div>
      </aside>

      <!-- MAIN -->
      <div class="main-area">
        <header class="topbar">
          <div class="topbar-brand">
            <img v-if="client.societe.logo" :src="client.societe.logo" :alt="client.societe.nom" class="topbar-logo" />
            <span v-else class="topbar-name">{{ client.societe.nom }}</span>
          </div>
          <div class="topbar-avatar">{{ client.prenom[0] }}{{ client.nom[0] }}</div>
        </header>

        <main class="content">

          <!-- ══════════════════════════════════ ACCUEIL ══════════════════════════════════ -->
          <section v-if="activeTab === 'home'" class="page" key="home">
            <div class="page-welcome">
              <div>
                <h1 class="welcome-title">Bonjour, <span class="capitalize">{{ client.prenom }}</span> 👋</h1>
                <p class="welcome-sub">Voici le suivi complet de vos travaux et interventions.</p>
              </div>
              <p class="welcome-date">{{ todayFr }}</p>
            </div>

            <!-- KPIs -->
            <div class="kpi-row">
              <div class="kpi-card" @click="goTab('chantiers')" style="cursor:pointer">
                <div class="kpi-icon" style="background:#eff6ff;color:#2563eb">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
                <div>
                  <p class="kpi-num">{{ kpis.chantiers }}</p>
                  <p class="kpi-lbl">Chantiers</p>
                </div>
              </div>
              <div class="kpi-card">
                <div class="kpi-icon" style="background:#f0fdf4;color:#16a34a">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                </div>
                <div>
                  <p class="kpi-num">{{ kpis.interventions }}</p>
                  <p class="kpi-lbl">Interventions</p>
                </div>
              </div>
              <div class="kpi-card">
                <div class="kpi-icon" style="background:#faf5ff;color:#7c3aed">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div>
                  <p class="kpi-num">{{ kpis.documents }}</p>
                  <p class="kpi-lbl">Documents</p>
                </div>
              </div>
              <div class="kpi-card" v-if="kpis.montant_impaye > 0" style="border-color:#fee2e2;">
                <div class="kpi-icon" style="background:#fff1f2;color:#e11d48">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                </div>
                <div>
                  <p class="kpi-num" style="color:#e11d48">{{ kpis.montant_impaye.toFixed(0) }} €</p>
                  <p class="kpi-lbl">Impayé</p>
                </div>
              </div>
              <div class="kpi-card" v-else>
                <div class="kpi-icon" style="background:#fffbeb;color:#d97706">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                </div>
                <div>
                  <p class="kpi-num">{{ kpis.montant_total.toFixed(0) }} €</p>
                  <p class="kpi-lbl">Total TTC</p>
                </div>
              </div>
            </div>

            <!-- Chantier actif -->
            <div v-if="chantierActif" class="home-highlight">
              <div class="highlight-badge">
                <span class="pulse-dot"></span>
                Chantier en cours
              </div>
              <div class="highlight-body">
                <div class="highlight-left">
                  <p class="highlight-adresse">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {{ chantierAdresse(chantierActif) }}
                  </p>
                  <div class="highlight-stats">
                    <span class="hstat">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                      {{ chantierActif.stats.nb_interventions }} intervention{{ chantierActif.stats.nb_interventions > 1 ? 's' : '' }}
                    </span>
                    <span class="hstat">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>
                      {{ chantierActif.stats.nb_docs }} doc{{ chantierActif.stats.nb_docs > 1 ? 's' : '' }}
                    </span>
                    <span v-if="chantierActif.stats.derniere_intervention" class="hstat">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      Dernière : {{ fmt(chantierActif.stats.derniere_intervention) }}
                    </span>
                  </div>
                </div>
                <button class="btn-primary-sm" @click="goChantier(chantierActif.id)">
                  Voir le dossier →
                </button>
              </div>

              <!-- Mini timeline des dernières interventions -->
              <div v-if="chantierActif.interventions.length" class="mini-timeline">
                <div v-for="(it, i) in chantierActif.interventions.slice(0, 3)" :key="it.id" class="mini-tl-item" :style="{ animationDelay: i * 60 + 'ms' }">
                  <div class="mini-tl-dot" :style="{ background: it.statut?.couleur || '#94a3b8' }"></div>
                  <div class="mini-tl-info">
                    <p class="mini-tl-type">{{ it.intervention_type.nom }}</p>
                    <p class="mini-tl-date">{{ fmt(it.date_intervention) }}</p>
                  </div>
                  <div v-if="it.statut" class="status-tag status-tag--sm" :style="statusStyle(it.statut)">
                    {{ it.statut.libelle }}
                  </div>
                </div>
                <button v-if="chantierActif.interventions.length > 3" class="mini-tl-more" @click="goChantier(chantierActif.id)">
                  + {{ chantierActif.interventions.length - 3 }} intervention{{ chantierActif.interventions.length - 3 > 1 ? 's' : '' }} supplémentaire{{ chantierActif.interventions.length - 3 > 1 ? 's' : '' }}
                </button>
              </div>
            </div>

            <!-- Dernière intervention (si pas de chantier actif) -->
            <div v-else-if="derniereIntervention" class="panel">
              <div class="panel-head">
                <p class="panel-label">Dernière intervention</p>
                <div v-if="derniereIntervention.statut" class="status-tag" :style="statusStyle(derniereIntervention.statut)">
                  <span class="status-dot" :style="{ background: derniereIntervention.statut.couleur }"></span>
                  {{ derniereIntervention.statut.libelle }}
                </div>
              </div>
              <p class="panel-title">{{ derniereIntervention.intervention_type.nom }}</p>
              <div class="meta-row">
                <span class="meta-chip"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>{{ fmt(derniereIntervention.date_intervention) }}</span>
                <span v-if="derniereIntervention.appareil" class="meta-chip"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>{{ derniereIntervention.appareil.marque }} {{ derniereIntervention.appareil.model }}</span>
              </div>
              <button class="btn-ghost" @click="selIntervention = derniereIntervention; $modal.show('modal-intervention')">Voir le détail →</button>
            </div>

            <!-- Résumé chantiers passés -->
            <div v-if="chantiersTermines.length" class="panel">
              <div class="panel-head">
                <p class="panel-label">Chantiers terminés</p>
                <span class="count-badge">{{ chantiersTermines.length }}</span>
              </div>
              <div class="chantier-summary-list">
                <div v-for="c in chantiersTermines.slice(0,3)" :key="c.id" class="chantier-summary-row" @click="goChantier(c.id)">
                  <div class="cs-left">
                    <p class="cs-adresse">{{ chantierAdresse(c) }}</p>
                    <p class="cs-meta">{{ c.stats.nb_interventions }} intervention{{ c.stats.nb_interventions > 1 ? 's' : '' }} · {{ fmt(c.stats.derniere_intervention) }}</p>
                  </div>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:#94a3b8;flex-shrink:0"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </div>
              <button v-if="chantiersTermines.length > 3" class="btn-ghost" @click="goTab('chantiers')" style="margin-top:8px">Voir tous les chantiers →</button>
            </div>
          </section>

          <!-- ══════════════════════════════════ CHANTIERS ══════════════════════════════════ -->
          <section v-if="activeTab === 'chantiers'" class="page" key="chantiers">

            <!-- Vue détail d'un chantier -->
            <div v-if="activeChantier" key="chantier-detail">
              <div class="breadcrumb" @click="activeChantier = null">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                Retour aux chantiers
              </div>

              <div class="chantier-detail-header">
                <div class="chantier-detail-title-row">
                  <div>
                    <div class="chantier-detail-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    </div>
                    <h1 class="chantier-detail-adresse">{{ chantierAdresse(activeChantier) }}</h1>
                    <p class="chantier-detail-date">Ouvert le {{ fmt(activeChantier.created_at) }}</p>
                  </div>
                  <div class="chantier-detail-status-wrap">
                    <div v-if="activeChantier.statut" class="status-tag" :style="statusStyle(activeChantier.statut)">
                      <span class="status-dot" :style="{ background: activeChantier.statut.couleur }"></span>
                      {{ activeChantier.statut.libelle }}
                    </div>
                    <div v-else-if="activeChantier.terminer" class="status-tag" style="background:#f0fdf418;color:#16a34a;border-color:#bbf7d0">
                      <span class="status-dot" style="background:#16a34a"></span>
                      Terminé
                    </div>
                    <div v-else class="status-tag" style="background:#eff6ff18;color:#2563eb;border-color:#bfdbfe">
                      <span class="status-dot" style="background:#2563eb"></span>
                      En cours
                    </div>
                  </div>
                </div>

                <!-- Stats du chantier -->
                <div class="chantier-kpi-row">
                  <div class="ckpi">
                    <p class="ckpi-n">{{ activeChantier.stats.nb_interventions }}</p>
                    <p class="ckpi-l">Intervention{{ activeChantier.stats.nb_interventions > 1 ? 's' : '' }}</p>
                  </div>
                  <div class="ckpi-sep"></div>
                  <div class="ckpi">
                    <p class="ckpi-n">{{ activeChantier.stats.nb_docs }}</p>
                    <p class="ckpi-l">Document{{ activeChantier.stats.nb_docs > 1 ? 's' : '' }}</p>
                  </div>
                  <div class="ckpi-sep"></div>
                  <div class="ckpi">
                    <p class="ckpi-n">{{ activeChantier.stats.nb_factures }}</p>
                    <p class="ckpi-l">Facture{{ activeChantier.stats.nb_factures > 1 ? 's' : '' }}</p>
                  </div>
                  <div class="ckpi-sep"></div>
                  <div class="ckpi">
                    <p class="ckpi-n">{{ activeChantier.stats.montant_total.toFixed(0) }} €</p>
                    <p class="ckpi-l">Total TTC</p>
                  </div>
                </div>
              </div>

              <!-- TABS internes chantier -->
              <div class="inner-tabs">
                <button v-for="t in chantierTabs" :key="t.id" @click="activeChantierTab = t.id" class="inner-tab" :class="{ 'inner-tab--on': activeChantierTab === t.id }">
                  {{ t.label }}
                  <span v-if="t.count" class="inner-tab-count">{{ t.count }}</span>
                </button>
              </div>

              <!-- TIMELINE -->
              <div v-if="activeChantierTab === 'timeline'" class="timeline-wrap">
                <div v-if="!activeChantier.interventions.length" class="empty-panel">
                  <p>Aucune intervention enregistrée sur ce chantier.</p>
                </div>
                <div class="timeline">
                  <div
                    v-for="(it, i) in activeChantier.interventions"
                    :key="it.id"
                    class="tl-item"
                    :style="{ animationDelay: i * 50 + 'ms' }"
                  >
                    <div class="tl-left">
                      <div class="tl-dot" :style="{ borderColor: it.statut?.couleur || '#cbd5e1', background: (it.statut?.couleur || '#94a3b8') + '20' }">
                        <div class="tl-dot-inner" :style="{ background: it.statut?.couleur || '#94a3b8' }"></div>
                      </div>
                      <div class="tl-line" v-if="i < activeChantier.interventions.length - 1"></div>
                    </div>
                    <div class="tl-card" @click="selIntervention = it; $modal.show('modal-intervention')">
                      <div class="tl-card-head">
                        <div>
                          <p class="tl-date">{{ fmt(it.date_intervention) }}{{ it.heure_debut ? ' · ' + it.heure_debut : '' }}</p>
                          <p class="tl-type">{{ it.intervention_type.nom }}</p>
                        </div>
                        <div class="tl-card-right">
                          <div v-if="it.statut" class="status-tag status-tag--sm" :style="statusStyle(it.statut)">
                            {{ it.statut.libelle }}
                          </div>
                          <svg class="tl-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </div>
                      </div>
                      <div class="tl-meta">
                        <span v-if="it.technicien" class="meta-chip">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          {{ it.technicien.prenom }} {{ it.technicien.nom }}
                        </span>
                        <span v-if="it.appareil" class="meta-chip">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                          {{ it.appareil.marque }} {{ it.appareil.model }}
                        </span>
                        <span v-if="it.documents && it.documents.length" class="meta-chip">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>
                          {{ it.documents.length }} doc{{ it.documents.length > 1 ? 's' : '' }}
                        </span>
                      </div>
                      <div v-if="it.intervention_type.description" class="tl-desc">
                        {{ it.intervention_type.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- DOCUMENTS du chantier -->
              <div v-if="activeChantierTab === 'documents'">
                <div v-if="!allChantierDocs.length" class="empty-panel"><p>Aucun document sur ce chantier.</p></div>
                <div v-else class="docs-grid">
                  <a
                    v-for="d in allChantierDocs" :key="d.id"
                    :href="d.url" target="_blank"
                    class="doc-card"
                  >
                    <div class="doc-card-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <div class="doc-card-info">
                      <p class="doc-card-type">{{ docTypeLabel[d.type] || d.type }}</p>
                      <p class="doc-card-date">{{ fmtShort(d.created_at) }}</p>
                    </div>
                    <span class="doc-card-dl">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </span>
                  </a>
                </div>
              </div>

              <!-- FACTURES du chantier -->
              <div v-if="activeChantierTab === 'factures'">
                <div v-if="!activeChantier.factures.length" class="empty-panel"><p>Aucune facture sur ce chantier.</p></div>
                <div v-else class="facture-table">
                  <div class="facture-table-head">
                    <span>Référence</span><span>Date</span><span>Statut</span><span style="text-align:right">Montant TTC</span><span></span>
                  </div>
                  <div v-for="f in activeChantier.factures" :key="f.id" class="facture-row">
                    <span class="facture-ref">{{ f.reference ? 'N°' + f.reference : 'Facture' }}</span>
                    <span class="facture-date">{{ fmt(f.created_at) }}</span>
                    <span><span class="badge" :class="sc(f.status)">{{ inv[f.status] || f.status }}</span></span>
                    <span class="facture-amount">{{ f.total_ttc }} €</span>
                    <span>
                      <a v-if="f.document_url" :href="f.document_url" target="_blank" class="dl-btn">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        PDF
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vue liste des chantiers -->
            <div v-else key="chantiers-list">
              <div class="page-header">
                <div>
                  <h1 class="page-title">Mes chantiers</h1>
                  <p class="page-sub">{{ client.chantiers.length }} dossier{{ client.chantiers.length > 1 ? 's' : '' }} au total</p>
                </div>
              </div>

              <div v-if="chantiersEnCours.length">
                <p class="list-section-label">En cours</p>
                <div class="chantier-card-list">
                  <div v-for="(c, i) in chantiersEnCours" :key="c.id" class="chantier-card chantier-card--active" :style="{ animationDelay: i * 40 + 'ms' }" @click="goChantier(c.id)">
                    <div class="chantier-card-body">
                      <div class="chantier-card-icon">
                        <span class="pulse-dot pulse-dot--sm"></span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      </div>
                      <div class="chantier-card-info">
                        <p class="chantier-card-adresse">{{ chantierAdresse(c) }}</p>
                        <p class="chantier-card-meta">
                          Ouvert le {{ fmt(c.created_at) }} ·
                          {{ c.stats.nb_interventions }} intervention{{ c.stats.nb_interventions > 1 ? 's' : '' }}
                        </p>
                        <div class="chantier-card-pills">
                          <span class="pill pill--blue">{{ c.stats.nb_interventions }} interv.</span>
                          <span class="pill pill--purple">{{ c.stats.nb_docs }} docs</span>
                          <span v-if="c.stats.nb_factures" class="pill pill--amber">{{ c.stats.nb_factures }} facture{{ c.stats.nb_factures > 1 ? 's' : '' }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="chantier-card-foot">
                      <div v-if="c.stats.derniere_intervention" class="chantier-card-last">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        Dernière intervention : {{ fmt(c.stats.derniere_intervention) }}
                      </div>
                      <span class="chantier-card-cta">Voir le dossier →</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="chantiersTermines.length" style="margin-top:24px">
                <p class="list-section-label">Terminés</p>
                <div class="chantier-card-list">
                  <div v-for="(c, i) in chantiersTermines" :key="c.id" class="chantier-card" :style="{ animationDelay: i * 40 + 'ms' }" @click="goChantier(c.id)">
                    <div class="chantier-card-body">
                      <div class="chantier-card-icon chantier-card-icon--done">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <div class="chantier-card-info">
                        <p class="chantier-card-adresse">{{ chantierAdresse(c) }}</p>
                        <p class="chantier-card-meta">
                          {{ fmt(c.stats.premiere_intervention) }} → {{ fmt(c.stats.derniere_intervention) }} ·
                          {{ c.stats.nb_interventions }} intervention{{ c.stats.nb_interventions > 1 ? 's' : '' }}
                        </p>
                        <div class="chantier-card-pills">
                          <span class="pill pill--gray">{{ c.stats.nb_interventions }} interv.</span>
                          <span class="pill pill--gray">{{ c.stats.nb_docs }} docs</span>
                          <span v-if="c.stats.montant_total" class="pill pill--gray">{{ c.stats.montant_total.toFixed(0) }} €</span>
                        </div>
                      </div>
                    </div>
                    <div class="chantier-card-foot">
                      <span class="chantier-card-cta" style="color:#94a3b8">Consulter l'historique →</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!client.chantiers.length" class="empty-panel">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" opacity=".3"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                <p>Aucun chantier enregistré.</p>
              </div>
            </div>
          </section>

          <!-- ══════════════════════════════════ DOCUMENTS ══════════════════════════════════ -->
          <section v-if="activeTab === 'documents'" class="page" key="documents">
            <div class="page-header">
              <div>
                <h1 class="page-title">Documents</h1>
                <p class="page-sub">{{ allDocuments.length }} document{{ allDocuments.length > 1 ? 's' : '' }} au total</p>
              </div>
            </div>
            <div v-if="!allDocuments.length" class="empty-panel">
              <p>Aucun document disponible.</p>
            </div>
            <!-- Groupés par chantier -->
            <div v-for="c in client.chantiers.filter(c => chantierAllDocs(c).length)" :key="c.id" class="doc-group">
              <div class="doc-group-header">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                {{ chantierAdresse(c) }}
                <span class="doc-group-count">{{ chantierAllDocs(c).length }}</span>
              </div>
              <div class="docs-grid">
                <a v-for="d in chantierAllDocs(c)" :key="d.id" :href="d.url" target="_blank" class="doc-card">
                  <div class="doc-card-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div class="doc-card-info">
                    <p class="doc-card-type">{{ docTypeLabel[d.type] || d.type }}</p>
                    <p class="doc-card-date">{{ fmtShort(d.created_at) }}</p>
                  </div>
                  <span class="doc-card-dl">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  </span>
                </a>
              </div>
            </div>
          </section>

          <!-- ══════════════════════════════════ FACTURES ══════════════════════════════════ -->
          <section v-if="activeTab === 'factures'" class="page" key="factures">
            <div class="page-header">
              <div>
                <h1 class="page-title">Factures</h1>
                <p class="page-sub">Historique de vos factures et paiements.</p>
              </div>
            </div>
            <div v-if="!allFactures.length" class="empty-panel">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" opacity=".3"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              <p>Aucune facture disponible.</p>
            </div>
            <!-- Résumé financier -->
            <div v-if="allFactures.length" class="finance-summary">
              <div class="finance-kpi">
                <p class="finance-kpi-n">{{ allFactures.reduce((a, f) => a + parseFloat(f.total_ttc || 0), 0).toFixed(2) }} €</p>
                <p class="finance-kpi-l">Total TTC</p>
              </div>
              <div class="finance-kpi" v-if="kpis.montant_impaye > 0" style="border-left:3px solid #ef4444">
                <p class="finance-kpi-n" style="color:#ef4444">{{ kpis.montant_impaye.toFixed(2) }} €</p>
                <p class="finance-kpi-l">En attente de règlement</p>
              </div>
              <div class="finance-kpi" v-else>
                <p class="finance-kpi-n" style="color:#16a34a">Tout réglé ✓</p>
                <p class="finance-kpi-l">Aucun impayé</p>
              </div>
            </div>
            <div v-if="allFactures.length" class="facture-table">
              <div class="facture-table-head">
                <span>Référence</span><span>Date</span><span>Statut</span><span style="text-align:right">Montant TTC</span><span></span>
              </div>
              <div v-for="f in allFactures" :key="f.id" class="facture-row">
                <span class="facture-ref">{{ f.reference ? 'Facture n°' + f.reference : 'Facture' }}</span>
                <span class="facture-date">{{ fmt(f.created_at) }}</span>
                <span><span class="badge" :class="sc(f.status)">{{ inv[f.status] || f.status }}</span></span>
                <span class="facture-amount">{{ f.total_ttc }} €</span>
                <span>
                  <a v-if="f.document_url" :href="f.document_url" target="_blank" class="dl-btn">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    PDF
                  </a>
                </span>
              </div>
            </div>
          </section>

          <!-- ══════════════════════════════════ APPAREILS ══════════════════════════════════ -->
          <section v-if="activeTab === 'appareils'" class="page" key="appareils">
            <div class="page-header">
              <div>
                <h1 class="page-title">Appareils</h1>
                <p class="page-sub">{{ client.appareils.length }} équipement{{ client.appareils.length > 1 ? 's' : '' }}</p>
              </div>
            </div>
            <div v-if="!client.appareils.length" class="empty-panel"><p>Aucun appareil enregistré.</p></div>
            <div class="eq-grid">
              <div v-for="a in client.appareils" :key="a.id" class="eq-card" :class="{ 'eq-card--open': selEq && selEq.id === a.id }" @click="selEq = selEq && selEq.id === a.id ? null : a">
                <div class="eq-card-row">
                  <div class="eq-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                  </div>
                  <div class="eq-info">
                    <p class="eq-name">{{ a.marque }} {{ a.model }}</p>
                    <p class="eq-type">{{ a.type }}</p>
                  </div>
                  <svg class="eq-chevron" :class="{ 'eq-chevron--open': selEq && selEq.id === a.id }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                <div v-if="selEq && selEq.id === a.id" class="eq-detail" @click.stop>
                  <div class="info-grid" style="padding-top:16px;">
                    <div v-if="a.serial || a.numero_serie" class="info-cell">
                      <p class="info-label">N° de série</p>
                      <p class="info-value">{{ a.serial || a.numero_serie }}</p>
                    </div>
                    <div v-if="a.installation_date" class="info-cell">
                      <p class="info-label">Installation</p>
                      <p class="info-value">{{ a.installation_date }}</p>
                    </div>
                  </div>
                  <!-- Interventions liées à cet appareil -->
                  <div class="eq-interventions">
                    <p class="section-label" style="margin-bottom:10px">Interventions associées</p>
                    <div class="eq-inter-list">
                      <div
                        v-for="it in interventionsForAppareil(a.id)"
                        :key="it.id"
                        class="eq-inter-row"
                        @click="selIntervention = it; $modal.show('modal-intervention')"
                      >
                        <div class="eq-inter-dot" :style="{ background: it.statut?.couleur || '#94a3b8' }"></div>
                        <div class="eq-inter-info">
                          <p class="eq-inter-type">{{ it.intervention_type.nom }}</p>
                          <p class="eq-inter-date">{{ fmt(it.date_intervention) }}</p>
                        </div>
                        <div v-if="it.statut" class="status-tag status-tag--sm" :style="statusStyle(it.statut)">{{ it.statut.libelle }}</div>
                      </div>
                      <p v-if="!interventionsForAppareil(a.id).length" class="eq-no-inter">Aucune intervention sur cet appareil.</p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </section>

          <!-- ══════════════════════════════════ PROFIL ══════════════════════════════════ -->
          <section v-if="activeTab === 'profil'" class="page" key="profil">
            <div class="page-header"><div><h1 class="page-title">Mon profil</h1><p class="page-sub">Vos coordonnées enregistrées.</p></div></div>
            <div class="profile-card">
              <div class="profile-hero">
                <div class="big-avatar">{{ client.prenom[0] }}{{ client.nom[0] }}</div>
                <div>
                  <p class="profile-name">{{ client.prenom }} {{ client.nom }}</p>
                  <p class="profile-company">Client {{ client.societe.nom }}</p>
                </div>
              </div>
              <div class="profile-fields">
                <div class="profile-field">
                  <div class="field-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></div>
                  <div><p class="field-label">Adresse e-mail</p><p class="field-value">{{ client.email }}</p></div>
                </div>
                <div class="profile-field">
                  <div class="field-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.5 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.72-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></div>
                  <div><p class="field-label">Téléphone</p><p class="field-value">{{ client.telephone }}</p></div>
                </div>
                <div v-if="client.adresses && client.adresses.length" class="profile-field" v-for="(addr, addrIndex) in client.adresses" :key="addr.id">
                  <div class="field-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
                  <div><p class="field-label">Adresse #{{ addrIndex + 1 }}</p><p class="field-value">{{ addr.adresse1 }}, {{ addr.code_postal }} {{ addr.ville }}</p></div>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>

      <!-- BOTTOM NAV (mobile) -->
      <nav class="bottom-nav">
        <button v-for="t in tabs" :key="t.id" @click="goTab(t.id)" class="bottom-btn" :class="{ 'bottom-btn--on': activeTab === t.id }">
          <span class="bottom-ico" v-html="t.svg"></span>
          <span class="bottom-lbl">{{ t.label }}</span>
        </button>
      </nav>

    </div>
  </div>
</template>

<script>
import DocType from './../../config/docType.js';

export default {
  data() {
    return {
      loading: false,
      client: null,
      kpis: {},
      derniereIntervention: null,
      chantierActif: null,
      activeTab: 'home',
      activeChantier: null,
      activeChantierTab: 'timeline',
      selIntervention: null,
      selEq: null,
      docTypeLabel: DocType,
      tabs: [],
      inv: {
        to_invoice: 'À facturer', to_relance: 'À relancer', retard: 'En retard',
        issued: 'Créée', sent: 'Impayée', relance: 'Relancée', paid: 'Payée',
      }
    };
  },
  computed: {
    chantiersEnCours() {
      return (this.client?.chantiers || []).filter(c => !c.terminer);
    },
    chantiersTermines() {
      return (this.client?.chantiers || []).filter(c => c.terminer);
    },
    allInterventions() {
      return (this.client?.chantiers || []).flatMap(c => c.interventions || []);
    },
    allDocuments() {
      return (this.client?.chantiers || []).flatMap(c => this.chantierAllDocs(c));
    },
    allFactures() {
      const fromChantiers = (this.client?.chantiers || []).flatMap(c => c.factures || []);
      const direct = this.client?.factures || [];
      return [...fromChantiers, ...direct].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    allChantierDocs() {
      return this.activeChantier ? this.chantierAllDocs(this.activeChantier) : [];
    },
    chantierTabs() {
      if (!this.activeChantier) return [];
      return [
        { id: 'timeline', label: 'Timeline', count: this.activeChantier.interventions.length },
        { id: 'documents', label: 'Documents', count: this.chantierAllDocs(this.activeChantier).length },
        { id: 'factures', label: 'Factures', count: this.activeChantier.factures.length },
      ];
    },
    todayFr() {
      return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
    },
  },
  methods: {
    buildTabs() {
      const impayes = this.allFactures.filter(f => ['sent','retard','to_relance','relance'].includes(f.status)).length;
      this.tabs = [
        { id: 'home', label: 'Accueil', svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>` },
        { id: 'chantiers', label: 'Chantiers', count: this.chantiersEnCours.length || undefined, svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>` },
        { id: 'documents', label: 'Documents', svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>` },
        { id: 'factures', label: 'Factures', count: impayes || undefined, svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>` },
        { id: 'appareils', label: 'Appareils', svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>` },
        { id: 'profil', label: 'Profil', svg: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>` },
      ];
    },
    async getSpace() {
      this.loading = true;
      try {
        const r = await this.$request.post('/dashboard-client/get-by-token', { token: this.$route.params.token });
        this.client = r.client;
        this.kpis = r.kpis || {};
        this.derniereIntervention = r.derniere_intervention || null;
        this.chantierActif = r.chantier_actif || null;
        this.selEq = this.client.appareils?.[0] || null;
        this.buildTabs();
      } finally {
        this.loading = false;
      }
    },
    goTab(id) {
      this.activeTab = id;
      this.activeChantier = null;
    },
    goChantier(id) {
      const c = this.client.chantiers.find(ch => ch.id === id);
      if (c) {
        this.activeChantier = c;
        this.activeChantierTab = 'timeline';
        this.activeTab = 'chantiers';
      }
    },
    chantierAdresse(c) {
      if (!c.adresse) return `Chantier #${c.id}`;
      return `${c.adresse.adresse1}, ${c.adresse.ville}`;
    },
    chantierAllDocs(c) {
      const direct = c.documents || [];
      const fromInter = (c.interventions || []).flatMap(i => i.documents || []);
      return [...direct, ...fromInter];
    },
    interventionsForAppareil(appareilId) {
      return this.allInterventions.filter(i => i.appareil && i.appareil.id === appareilId);
    },
    statusStyle(statut) {
      return {
        background: statut.couleur + '18',
        color: statut.couleur,
        borderColor: statut.couleur + '40',
      };
    },
    fmt(d) {
      if (!d) return '—';
      return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(d));
    },
    fmtShort(d) {
      if (!d) return '—';
      return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(d));
    },
    sc(s) {
      return { paid: 'badge--green', sent: 'badge--red', issued: 'badge--gray', to_relance: 'badge--amber', relance: 'badge--amber', retard: 'badge--red', to_invoice: 'badge--gray' }[s] || 'badge--gray';
    },
  },
  mounted() {
    this.getSpace();
    if (this.$account?.account?.societe?.logo) {
      const l = document.querySelector("link[rel~='icon']");
      if (l) l.remove();
      const nl = document.createElement('link');
      nl.rel = 'icon'; nl.type = 'image/png';
      nl.href = this.$account.account.societe.logo;
      document.head.appendChild(nl);
    }
    document.title = 'Espace client · ' + (this.$account?.account?.societe?.nom || '');
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.root {
  --bg:           #f1f2f6;
  --surface:      #ffffff;
  --surface-2:    #f7f8fa;
  --border:       rgba(0,0,0,0.07);
  --border-2:     rgba(0,0,0,0.13);
  --text:         #0f0f14;
  --text-2:       #3d3d4d;
  --text-3:       #8888a0;
  --accent:       #3b5bdb;
  --accent-light: #edf2ff;
  --accent-dk:    #3451c7;
  --sidebar-w:    248px;
  --topbar-h:     56px;
  --bnav-h:       68px;
  --r:            14px;
  --r-sm:         10px;
  --shadow-sm:    0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04);
  --shadow:       0 4px 20px rgba(0,0,0,0.07);
  --shadow-lg:    0 12px 40px rgba(0,0,0,0.1);
  --font:         'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
  min-height: 100vh;
  background: var(--bg);
  font-family: var(--font);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}

/* ─── LOADING ─── */
.loading-state { min-height:100vh; display:flex; align-items:center; justify-content:center; }
.loader-ring { display:inline-block; position:relative; width:48px; height:48px; }
.loader-ring div { box-sizing:border-box; display:block; position:absolute; width:40px; height:40px; margin:4px; border:3px solid transparent; border-top-color:var(--accent); border-radius:50%; animation:spin .9s cubic-bezier(.5,0,.5,1) infinite; }
.loader-ring div:nth-child(1){animation-delay:-.3s}.loader-ring div:nth-child(2){animation-delay:-.2s}.loader-ring div:nth-child(3){animation-delay:-.1s}
@keyframes spin { to { transform:rotate(360deg); } }

/* ─── SHELL ─── */
.app-shell { display:flex; min-height:100vh; }

/* ─── SIDEBAR ─── */
.sidebar { width:var(--sidebar-w); flex-shrink:0; background:var(--surface); border-right:1px solid var(--border); display:flex; flex-direction:column; position:fixed; top:0; left:0; height:100vh; z-index:200; }
.sidebar-top { flex:1; display:flex; flex-direction:column; overflow:hidden; }
.sidebar-brand { height:64px; display:flex; align-items:center; padding:0 20px; border-bottom:1px solid var(--border); flex-shrink:0; }
.brand-logo { height:28px; object-fit:contain; }
.brand-name { font-size:16px; font-weight:700; color:var(--text); letter-spacing:-.02em; }
.side-nav { flex:1; padding:12px 10px; display:flex; flex-direction:column; gap:1px; overflow-y:auto; }
.side-link { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:var(--r-sm); border:none; background:none; cursor:pointer; width:100%; color:var(--text-3); font-family:var(--font); font-size:14px; font-weight:500; transition:background .15s, color .15s; text-align:left; }
.side-link:hover { background:var(--surface-2); color:var(--text-2); }
.side-link--active { background:var(--accent-light) !important; color:var(--accent) !important; font-weight:600; }
.side-ico { line-height:0; flex-shrink:0; }
.side-lbl { flex:1; }
.side-badge { background:var(--accent); color:#fff; font-size:11px; font-weight:700; min-width:18px; height:18px; border-radius:9px; display:flex; align-items:center; justify-content:center; padding:0 5px; }
.sidebar-user { display:flex; align-items:center; gap:10px; padding:14px 20px; border-top:1px solid var(--border); flex-shrink:0; }
.user-avatar { width:36px; height:36px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,var(--accent),var(--accent-dk)); color:#fff; font-size:14px; font-weight:600; display:flex; align-items:center; justify-content:center; }
.user-info { min-width:0; }
.user-name { font-size:13px; font-weight:600; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.user-meta { font-size:11px; color:var(--text-3); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* ─── MAIN ─── */
.main-area { flex:1; margin-left:var(--sidebar-w); display:flex; flex-direction:column; min-height:100vh; }
.topbar { display:none; height:var(--topbar-h); align-items:center; justify-content:space-between; padding:0 18px; background:var(--surface); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
.topbar-logo { height:24px; object-fit:contain; }
.topbar-name { font-size:15px; font-weight:700; }
.topbar-avatar { width:32px; height:32px; border-radius:50%; background:linear-gradient(135deg,var(--accent),var(--accent-dk)); color:#fff; font-size:12px; font-weight:600; display:flex; align-items:center; justify-content:center; }
.content { flex:1; padding:32px 32px 52px; width:100%; }

/* ─── PAGE ─── */
.page { animation:fadeUp .28s cubic-bezier(.22,1,.36,1) both; display:flex; flex-direction:column; gap:20px; max-width:980px; margin:0 auto; width:100%; }
@keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
.page-welcome { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.welcome-title { font-size:28px; font-weight:700; letter-spacing:-.03em; color:var(--text); }
.welcome-sub { font-size:15px; color:var(--text-3); margin-top:4px; }
.welcome-date { font-size:13px; color:var(--text-3); text-transform:capitalize; margin-top:6px; white-space:nowrap; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; }
.page-title { font-size:26px; font-weight:700; letter-spacing:-.03em; }
.page-sub { font-size:14px; color:var(--text-3); margin-top:3px; }

/* ─── KPI ROW ─── */
.kpi-row { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:10px; }
.kpi-card { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); padding:16px; display:flex; align-items:center; gap:14px; cursor:default; transition:box-shadow .15s; }
.kpi-card:hover { box-shadow:var(--shadow); }
.kpi-icon { width:40px; height:40px; border-radius:10px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.kpi-num { font-size:24px; font-weight:700; letter-spacing:-.04em; color:var(--text); line-height:1; }
.kpi-lbl { font-size:12px; font-weight:500; color:var(--text-3); margin-top:2px; }

/* ─── HOME HIGHLIGHT (chantier actif) ─── */
.home-highlight { background:var(--surface); border-radius:var(--r); border:1.5px solid var(--accent-light); box-shadow:0 0 0 4px rgba(59,91,219,.05), var(--shadow-sm); overflow:hidden; }
.highlight-badge { display:flex; align-items:center; gap:8px; padding:10px 18px; background:var(--accent); color:#fff; font-size:12px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; }
.pulse-dot { width:8px; height:8px; border-radius:50%; background:rgba(255,255,255,.8); position:relative; flex-shrink:0; }
.pulse-dot::after { content:''; position:absolute; inset:-3px; border-radius:50%; background:rgba(255,255,255,.3); animation:pulse 1.5s ease-out infinite; }
@keyframes pulse { 0%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(2)} }
.pulse-dot--sm { width:6px; height:6px; background:var(--accent); }
.pulse-dot--sm::after { inset:-2px; }
.highlight-body { display:flex; align-items:center; justify-content:space-between; padding:16px 18px; gap:12px; flex-wrap:wrap; }
.highlight-adresse { display:flex; align-items:center; gap:6px; font-size:16px; font-weight:700; letter-spacing:-.02em; color:var(--text); margin-bottom:8px; }
.highlight-stats { display:flex; flex-wrap:wrap; gap:10px; }
.hstat { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:500; color:var(--text-3); }

/* ─── MINI TIMELINE ─── */
.mini-timeline { border-top:1px solid var(--border); padding:0 18px 4px; }
.mini-tl-item { display:flex; align-items:center; gap:12px; padding:11px 0; border-bottom:1px solid var(--border); animation:fadeUp .25s both; }
.mini-tl-item:last-child { border-bottom:none; }
.mini-tl-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.mini-tl-info { flex:1; }
.mini-tl-type { font-size:14px; font-weight:600; color:var(--text); letter-spacing:-.01em; }
.mini-tl-date { font-size:12px; color:var(--text-3); margin-top:1px; }
.mini-tl-more { width:100%; padding:10px; background:none; border:none; color:var(--accent); font-family:var(--font); font-size:13px; font-weight:600; cursor:pointer; text-align:center; }
.mini-tl-more:hover { text-decoration:underline; }

/* ─── PANEL ─── */
.panel { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); padding:20px; }
.panel-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px; }
.panel-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); }
.panel-title { font-size:20px; font-weight:700; letter-spacing:-.03em; margin-bottom:12px; }
.count-badge { background:var(--surface-2); border:1px solid var(--border); border-radius:980px; padding:2px 10px; font-size:12px; font-weight:600; color:var(--text-2); }

/* ─── CHANTIER SUMMARY ─── */
.chantier-summary-list { display:flex; flex-direction:column; }
.chantier-summary-row { display:flex; align-items:center; gap:12px; padding:12px 0; border-bottom:1px solid var(--border); cursor:pointer; transition:background .15s; border-radius:var(--r-sm); padding-left:8px; }
.chantier-summary-row:last-child { border-bottom:none; }
.chantier-summary-row:hover { background:var(--surface-2); }
.cs-adresse { font-size:14px; font-weight:600; color:var(--text); }
.cs-meta { font-size:12px; color:var(--text-3); margin-top:2px; }

/* ─── BREADCRUMB ─── */
.breadcrumb { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:var(--text-3); cursor:pointer; margin-bottom:20px; transition:color .15s; }
.breadcrumb:hover { color:var(--accent); }

/* ─── CHANTIER DETAIL HEADER ─── */
.chantier-detail-header { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); overflow:hidden; margin-bottom:4px; }
.chantier-detail-title-row { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding:24px 24px 0; flex-wrap:wrap; }
.chantier-detail-icon { width:48px; height:48px; border-radius:13px; background:var(--accent-light); color:var(--accent); display:flex; align-items:center; justify-content:center; margin-bottom:12px; }
.chantier-detail-adresse { font-size:22px; font-weight:700; letter-spacing:-.03em; color:var(--text); }
.chantier-detail-date { font-size:13px; color:var(--text-3); margin-top:4px; }
.chantier-detail-status-wrap { flex-shrink:0; padding-top:4px; }
.chantier-kpi-row { display:flex; align-items:center; padding:18px 24px; border-top:1px solid var(--border); margin-top:20px; gap:0; }
.ckpi { flex:1; text-align:center; }
.ckpi-n { font-size:22px; font-weight:700; letter-spacing:-.04em; color:var(--text); line-height:1; }
.ckpi-l { font-size:11px; font-weight:500; color:var(--text-3); margin-top:3px; letter-spacing:.01em; }
.ckpi-sep { width:1px; background:var(--border); height:36px; }

/* ─── INNER TABS ─── */
.inner-tabs { display:flex; gap:4px; padding:4px; background:var(--surface-2); border-radius:var(--r); border:1px solid var(--border); width:fit-content; }
.inner-tab { padding:8px 18px; border-radius:10px; border:none; background:none; cursor:pointer; font-family:var(--font); font-size:13px; font-weight:500; color:var(--text-3); transition:background .15s, color .15s; display:flex; align-items:center; gap:7px; }
.inner-tab:hover { color:var(--text-2); }
.inner-tab--on { background:var(--surface); color:var(--text); font-weight:600; box-shadow:var(--shadow-sm); }
.inner-tab-count { background:var(--border); border-radius:980px; padding:1px 7px; font-size:11px; font-weight:700; color:var(--text-2); }

/* ─── LIST SECTION LABEL ─── */
.list-section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:var(--text-3); margin-bottom:10px; }

/* ─── CHANTIER CARDS ─── */
.chantier-card-list { display:flex; flex-direction:column; gap:10px; }
.chantier-card { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); overflow:hidden; cursor:pointer; transition:box-shadow .18s, border-color .18s; animation:fadeUp .25s both; }
.chantier-card:hover { box-shadow:var(--shadow); border-color:var(--border-2); }
.chantier-card--active { border-color:#bfdbfe; box-shadow:0 0 0 3px rgba(59,91,219,.06), var(--shadow-sm); }
.chantier-card-body { display:flex; align-items:flex-start; gap:14px; padding:18px 18px 14px; }
.chantier-card-icon { width:44px; height:44px; border-radius:12px; flex-shrink:0; background:var(--accent-light); color:var(--accent); display:flex; align-items:center; justify-content:center; position:relative; }
.chantier-card-icon--done { background:#f0fdf4; color:#16a34a; }
.chantier-card-info { flex:1; min-width:0; }
.chantier-card-adresse { font-size:16px; font-weight:700; letter-spacing:-.02em; color:var(--text); margin-bottom:4px; }
.chantier-card-meta { font-size:12px; color:var(--text-3); margin-bottom:10px; }
.chantier-card-pills { display:flex; flex-wrap:wrap; gap:6px; }
.pill { display:inline-block; padding:3px 10px; border-radius:980px; font-size:11px; font-weight:600; }
.pill--blue   { background:#eff6ff; color:#1d4ed8; }
.pill--purple { background:#f5f3ff; color:#6d28d9; }
.pill--amber  { background:#fffbeb; color:#b45309; }
.pill--gray   { background:#f3f4f6; color:#4b5563; }
.chantier-card-foot { display:flex; align-items:center; justify-content:space-between; padding:10px 18px 14px; border-top:1px solid var(--border); }
.chantier-card-last { display:flex; align-items:center; gap:5px; font-size:12px; color:var(--text-3); }
.chantier-card-cta { font-size:13px; font-weight:600; color:var(--accent); }

/* ─── TIMELINE ─── */
.timeline-wrap { padding-top:4px; }
.timeline { display:flex; flex-direction:column; }
.tl-item { display:flex; gap:16px; animation:fadeUp .25s both; }
.tl-left { display:flex; flex-direction:column; align-items:center; flex-shrink:0; width:24px; }
.tl-dot { width:20px; height:20px; border-radius:50%; border:2px solid; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.tl-dot-inner { width:7px; height:7px; border-radius:50%; }
.tl-line { flex:1; width:2px; background:var(--border); margin:4px 0; min-height:16px; }
.tl-card { flex:1; background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); padding:14px 16px; margin-bottom:12px; cursor:pointer; transition:box-shadow .18s, border-color .18s; }
.tl-card:hover { box-shadow:var(--shadow); border-color:var(--border-2); }
.tl-card-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:8px; }
.tl-card-right { display:flex; align-items:center; gap:8px; flex-shrink:0; }
.tl-date { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-3); margin-bottom:3px; }
.tl-type { font-size:16px; font-weight:700; letter-spacing:-.02em; color:var(--text); }
.tl-meta { display:flex; flex-wrap:wrap; gap:8px; margin-top:6px; }
.tl-desc { font-size:13px; color:var(--text-2); margin-top:8px; line-height:1.5; padding-top:8px; border-top:1px solid var(--border); }
.tl-arrow { color:var(--text-3); flex-shrink:0; }

/* ─── META CHIP ─── */
.meta-row { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.meta-chip { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:500; color:var(--text-3); }

/* ─── STATUS TAG ─── */
.status-tag { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:980px; font-size:12px; font-weight:600; letter-spacing:.01em; border:1px solid; white-space:nowrap; flex-shrink:0; }
.status-tag--sm { font-size:11px; padding:3px 8px; }
.status-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }

/* ─── DOCS GRID ─── */
.docs-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:10px; }
.doc-card { background:var(--surface); border-radius:var(--r-sm); border:1px solid var(--border); padding:14px; display:flex; align-items:center; gap:12px; text-decoration:none; color:var(--text); transition:box-shadow .15s, border-color .15s; }
.doc-card:hover { box-shadow:var(--shadow); border-color:var(--border-2); }
.doc-card-icon { width:38px; height:38px; border-radius:10px; background:var(--accent-light); color:var(--accent); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.doc-card-info { flex:1; min-width:0; }
.doc-card-type { font-size:13px; font-weight:600; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.doc-card-date { font-size:11px; color:var(--text-3); margin-top:2px; }
.doc-card-dl { color:var(--accent); flex-shrink:0; }

/* ─── DOC GROUP ─── */
.doc-group { margin-bottom:24px; }
.doc-group-header { display:flex; align-items:center; gap:7px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); margin-bottom:10px; }
.doc-group-count { background:var(--surface-2); border:1px solid var(--border); border-radius:980px; padding:1px 8px; font-size:11px; }

/* ─── DOC LIST ─── */
.doc-list { display:flex; flex-direction:column; gap:6px; }
.doc-row { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:var(--r-sm); background:var(--surface-2); text-decoration:none; color:var(--text); transition:background .15s; }
.doc-row:hover { background:#e8e9f3; }
.doc-icon-wrap { width:28px; height:28px; border-radius:7px; background:var(--surface); display:flex; align-items:center; justify-content:center; box-shadow:var(--shadow-sm); color:var(--accent); flex-shrink:0; }
.doc-name { flex:1; font-size:13px; font-weight:500; }
.doc-open { font-size:12px; font-weight:600; color:var(--accent); white-space:nowrap; }

/* ─── FINANCE SUMMARY ─── */
.finance-summary { display:flex; gap:12px; flex-wrap:wrap; }
.finance-kpi { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); padding:16px 20px; flex:1; min-width:160px; }
.finance-kpi-n { font-size:24px; font-weight:700; letter-spacing:-.04em; color:var(--text); }
.finance-kpi-l { font-size:13px; color:var(--text-3); margin-top:4px; }

/* ─── FACTURE TABLE ─── */
.facture-table { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); overflow:hidden; }
.facture-table-head { display:grid; grid-template-columns:2fr 1.5fr 1fr 1fr 1.2fr; gap:12px; padding:11px 18px; background:var(--surface-2); border-bottom:1px solid var(--border); font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); }
.facture-row { display:grid; grid-template-columns:2fr 1.5fr 1fr 1fr 1.2fr; gap:12px; padding:13px 18px; border-bottom:1px solid var(--border); align-items:center; transition:background .15s; }
.facture-row:last-child { border-bottom:none; }
.facture-row:hover { background:var(--surface-2); }
.facture-ref { font-size:14px; font-weight:600; color:var(--text); }
.facture-date { font-size:13px; color:var(--text-2); }
.facture-amount { font-size:16px; font-weight:700; text-align:right; color:var(--text); }
.dl-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:980px; background:var(--accent-light); color:var(--accent); font-size:12px; font-weight:600; text-decoration:none; transition:background .15s; }
.dl-btn:hover { background:#d0e0ff; }

/* ─── BADGES ─── */
.badge { display:inline-block; padding:2px 8px; border-radius:980px; font-size:11px; font-weight:600; }
.badge--green  { background:#dcfce7; color:#166534; }
.badge--red    { background:#fee2e2; color:#991b1b; }
.badge--amber  { background:#fef3c7; color:#92400e; }
.badge--gray   { background:#f3f4f6; color:#374151; }

/* ─── EQ ─── */
.eq-grid { display:flex; flex-direction:column; gap:8px; }
.eq-card { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); overflow:hidden; cursor:pointer; transition:box-shadow .18s, border-color .18s; }
.eq-card:hover { box-shadow:var(--shadow); border-color:var(--border-2); }
.eq-card--open { border-color:var(--accent) !important; box-shadow:0 0 0 3px rgba(59,91,219,.07); }
.eq-card-row { display:flex; align-items:center; gap:14px; padding:16px 18px; }
.eq-icon { width:44px; height:44px; border-radius:11px; flex-shrink:0; background:var(--accent-light); color:var(--accent); display:flex; align-items:center; justify-content:center; }
.eq-info { flex:1; }
.eq-name { font-size:16px; font-weight:600; letter-spacing:-.01em; color:var(--text); }
.eq-type { font-size:12px; font-weight:500; color:var(--text-3); text-transform:uppercase; letter-spacing:.05em; margin-top:2px; }
.eq-chevron { color:var(--text-3); transition:transform .22s cubic-bezier(.22,1,.36,1); }
.eq-chevron--open { transform:rotate(180deg); }
.eq-detail { padding:0 18px 18px; border-top:1px solid var(--border); }
.eq-interventions { margin-top:16px; }
.eq-inter-list { display:flex; flex-direction:column; background:var(--surface-2); border-radius:var(--r-sm); overflow:hidden; }
.eq-inter-row { display:flex; align-items:center; gap:12px; padding:11px 14px; border-bottom:1px solid var(--border); cursor:pointer; transition:background .15s; }
.eq-inter-row:last-child { border-bottom:none; }
.eq-inter-row:hover { background:#e8e9f3; }
.eq-inter-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.eq-inter-info { flex:1; }
.eq-inter-type { font-size:13px; font-weight:600; color:var(--text); }
.eq-inter-date { font-size:11px; color:var(--text-3); margin-top:1px; }
.eq-no-inter { font-size:13px; color:var(--text-3); padding:14px; text-align:center; }

/* ─── INFO GRID ─── */
.info-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.info-cell { background:var(--surface-2); border-radius:var(--r-sm); padding:12px; }
.info-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); margin-bottom:4px; }
.info-value { font-size:14px; font-weight:600; color:var(--text); line-height:1.3; }
.section-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); }

/* ─── PROFILE ─── */
.profile-card { background:var(--surface); border-radius:var(--r); border:1px solid var(--border); box-shadow:var(--shadow-sm); overflow:hidden; }
.profile-hero { display:flex; align-items:center; gap:16px; padding:28px 24px 24px; background:linear-gradient(135deg,var(--accent-light) 0%,#fff 60%); border-bottom:1px solid var(--border); }
.big-avatar { width:60px; height:60px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,var(--accent),var(--accent-dk)); color:#fff; font-size:22px; font-weight:700; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 16px rgba(59,91,219,.25); }
.profile-name { font-size:20px; font-weight:700; letter-spacing:-.02em; color:var(--text); }
.profile-company { font-size:13px; color:var(--text-3); margin-top:3px; }
.profile-fields { padding:8px 0; }
.profile-field { display:flex; align-items:center; gap:14px; padding:16px 24px; border-bottom:1px solid var(--border); }
.profile-field:last-child { border-bottom:none; }
.field-icon { width:32px; height:32px; border-radius:8px; flex-shrink:0; background:var(--surface-2); display:flex; align-items:center; justify-content:center; color:var(--text-3); }
.field-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); margin-bottom:3px; }
.field-value { font-size:15px; font-weight:500; color:var(--text); }

/* ─── BUTTONS ─── */
.btn-primary-sm { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; background:var(--accent); color:#fff; border:none; border-radius:980px; font-family:var(--font); font-size:13px; font-weight:600; cursor:pointer; transition:background .15s; white-space:nowrap; flex-shrink:0; }
.btn-primary-sm:hover { background:var(--accent-dk); }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; background:none; color:var(--accent); border:1.5px solid rgba(59,91,219,.25); border-radius:980px; font-family:var(--font); font-size:13px; font-weight:600; cursor:pointer; transition:background .15s, border-color .15s; }
.btn-ghost:hover { background:var(--accent-light); border-color:var(--accent); }

/* ─── MODAL ─── */
.modal-wrap { background:var(--surface); border-radius:20px 20px 0 0; max-height:90vh; overflow-y:auto; }
.modal-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:22px 20px 16px; border-bottom:1px solid var(--border); position:sticky; top:0; background:var(--surface); z-index:10; }
.modal-header-left { min-width:0; }
.modal-ref { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-3); margin-bottom:4px; display:block; }
.modal-title { font-size:19px; font-weight:700; letter-spacing:-.02em; color:var(--text); }
.icon-btn { width:32px; height:32px; border-radius:50%; background:var(--surface-2); border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--text-3); flex-shrink:0; transition:background .15s; }
.icon-btn:hover { background:#e8e9f3; }
.modal-body { padding:18px 20px; display:flex; flex-direction:column; gap:16px; }
.modal-section { padding-top:16px; border-top:1px solid var(--border); display:flex; flex-direction:column; gap:10px; }
.modal-note { display:flex; align-items:flex-start; gap:8px; background:var(--surface-2); border-radius:var(--r-sm); padding:12px; font-size:13px; color:var(--text-2); line-height:1.5; }
.sig-img { max-width:100%; border-radius:var(--r-sm); border:1px solid var(--border); padding:8px; background:var(--surface-2); }

/* ─── EMPTY ─── */
.empty-panel { background:var(--surface); border-radius:var(--r); border:1.5px dashed var(--border-2); padding:48px 24px; display:flex; flex-direction:column; align-items:center; gap:12px; color:var(--text-3); font-size:15px; text-align:center; }

/* ─── BOTTOM NAV ─── */
.bottom-nav { display:none; position:fixed; bottom:0; left:0; right:0; z-index:200; height:var(--bnav-h); background:rgba(255,255,255,0.95); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border-top:1px solid var(--border); }
.bottom-btn { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3px; border:none; background:none; cursor:pointer; padding:6px 0; color:var(--text-3); font-family:var(--font); font-size:10px; font-weight:600; letter-spacing:.02em; text-transform:uppercase; transition:color .15s; -webkit-tap-highlight-color:transparent; }
.bottom-btn--on { color:var(--accent); }
.bottom-ico { line-height:0; }

/* ─── RESPONSIVE ─── */
@media (max-width:768px) {
  .sidebar { display:none; }
  .topbar { display:flex; }
  .bottom-nav { display:flex; }
  .main-area { margin-left:0; }
  .content { padding:16px 16px calc(var(--bnav-h) + 16px); }
  .kpi-row { grid-template-columns:1fr 1fr; }
  .welcome-title { font-size:22px; }
  .chantier-kpi-row { flex-wrap:wrap; gap:12px; }
  .ckpi { flex:1; min-width:80px; }
  .facture-table-head { display:none; }
  .facture-row { grid-template-columns:1fr 1fr; row-gap:6px; }
  .facture-row > span:nth-child(1) { grid-column:1; }
  .facture-row > span:nth-child(2) { grid-column:1; font-size:11px; }
  .facture-row > span:nth-child(3) { grid-column:2; grid-row:1; text-align:right; }
  .facture-row > span:nth-child(4) { grid-column:2; grid-row:2; text-align:right; }
  .facture-row > span:nth-child(5) { grid-column:1/-1; }
  .inner-tabs { flex-wrap:wrap; width:100%; }
  .inner-tab { flex:1; justify-content:center; }
  .docs-grid { grid-template-columns:1fr; }
  .chantier-detail-title-row { flex-direction:column; gap:10px; }
  .highlight-body { flex-direction:column; align-items:flex-start; }
}
@media (min-width:769px) and (max-width:1100px) {
  .content { padding:24px 24px 48px; }
}
</style>